import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import ResponsiveTooltip from '../components/ui/ResponsiveTooltip';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar, Plus, Clock, CheckSquare2, AlertCircle, XCircle, Users, Trash, FileText, Edit, Eye, AlertTriangle, MapPin, Video, Download, Loader } from 'lucide-react';
import Layout from '../components/Layout';
import CreateMeetingModal from '../components/CreateMeetingModal';
import EditMeetingModal from '../components/EditMeetingModal';
import MeetingCard from '../components/MeetingCard';
import CalendarView from '../components/CalendarView';
import MyTaskManagement from '../components/MyTask/MyTaskManagement';
import ReviewTasksTable from '../components/ReviewTasksTable';
import MOMModal from '../components/MOMModal';
import ActionPointsViewModal from '../components/ActionPointsViewModal';
import CancelRecurringMeetingDialog from '../components/CancelRecurringMeetingDialog';
import Pagination from '../components/Pagination';
import { meetingAPI, userAPI, executeInBatches } from '../Services/apiService';
import { showToast } from '../utils/toast';
import { formatRecurrence } from '../lib/utils';
import { format } from 'date-fns';
import { fetchEmployeeReviewCount } from '../utils/reviewCountHelper';
import { handleAPIError } from '../utils/errorHandler';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Calendar as CalendarPicker } from '../components/ui/calendar';


const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const urlSubtab = searchParams.get('subtab');
  const urlTaskId = searchParams.get('taskId');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    if (urlTab) return urlTab;
    // Initialize from localStorage if available
    const storedTab = localStorage.getItem('employeeDashboardTab');
    return storedTab || 'meetings';
  });

  // Update activeTab if urlTab changes
  useEffect(() => {
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab]);
  const [activeMeetingTab, setActiveMeetingTab] = useState('upcoming');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMOMModalOpen, setIsMOMModalOpen] = useState(false);
  const [isActionPointsViewModalOpen, setIsActionPointsViewModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [isEditMeetingModalOpen, setIsEditMeetingModalOpen] = useState(false);
  const [meetingToEdit, setMeetingToEdit] = useState(null);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isRecurringCancelDialogOpen, setIsRecurringCancelDialogOpen] = useState(false);
  const [meetingToCancel, setMeetingToCancel] = useState(null);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [employeeReviewCount, setEmployeeReviewCount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [exportDateRange, setExportDateRange] = useState({ from: null, to: null });
  const [loadingExport, setLoadingExport] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleExportMeetings = async () => {
    try {
      setLoadingExport(true);
      const params = {};
      if (exportDateRange?.from) params.start_date = format(exportDateRange.from, 'yyyy-MM-dd');
      if (exportDateRange?.to) params.end_date = format(exportDateRange.to, 'yyyy-MM-dd');

      const response = await userAPI.exportmeeting(params);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Meetings_Export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success('Meetings exported successfully');
    } catch (error) {
      handleAPIError(error, 'Failed to export meetings');
    } finally {
      setLoadingExport(false);
    }
  };

  // Reset pages when search term changes
  useEffect(() => {
    setUpcomingPage(1);
    setOngoingPage(1);
    setPendingPage(1);
    setCompletedPage(1);
    setCancelledPage(1);
  }, [debouncedSearchTerm]);

  // Pagination states for each tab
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [ongoingPage, setOngoingPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(1);
  const [cancelledPage, setCancelledPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Default 10 items per page

  // Pagination metadata
  const [upcomingTotal, setUpcomingTotal] = useState(0);
  const [ongoingTotal, setOngoingTotal] = useState(0);
  const [completedTotal, setCompletedTotal] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [cancelledTotal, setCancelledTotal] = useState(0);
  const [upcomingPages, setUpcomingPages] = useState(1);
  const [ongoingPages, setOngoingPages] = useState(1);
  const [completedPages, setCompletedPages] = useState(1);
  const [pendingPages, setPendingPages] = useState(1);
  const [cancelledPages, setCancelledPages] = useState(1);

  // Filtered totals for pagination (separate from global totals used in stats cards)
  const [upcomingFilteredTotal, setUpcomingFilteredTotal] = useState(0);
  const [ongoingFilteredTotal, setOngoingFilteredTotal] = useState(0);
  const [completedFilteredTotal, setCompletedFilteredTotal] = useState(0);
  const [pendingFilteredTotal, setPendingFilteredTotal] = useState(0);
  const [cancelledFilteredTotal, setCancelledFilteredTotal] = useState(0);
  const [upcomingFilteredPages, setUpcomingFilteredPages] = useState(1);
  const [ongoingFilteredPages, setOngoingFilteredPages] = useState(1);
  const [completedFilteredPages, setCompletedFilteredPages] = useState(1);
  const [pendingFilteredPages, setPendingFilteredPages] = useState(1);
  const [cancelledFilteredPages, setCancelledFilteredPages] = useState(1);

  // Fetch meetings for a specific state with optional search
  const fetchMeetingsByState = async (state, page, search = '') => {
    try {
      const response = await meetingAPI.getMeetings({
        state,
        page,
        page_size: pageSize,
        search: search,
      });

      console.log(`[${state}] Backend response:`, response.data);

      if (response.status === 200 && response.data) {
        // Check if backend returned paginated response (with items array) or direct array
        if (Array.isArray(response.data)) {
          // Backward compatibility: backend returned array directly
          console.warn(`[${state}] Backend returned array instead of paginated response`);
          return {
            items: response.data,
            total: response.data.length,
            pages: 1,
            page: 1,
            page_size: response.data.length,
            has_next: false,
            has_prev: false
          };
        }
        // Backend returned paginated response
        return response.data;
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch ${state} meetings:`, error);
      return null;
    }
  };

  // Save activeTab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('employeeDashboardTab', activeTab);
  }, [activeTab]);

  // Load all tabs counts on initial mount (for stats cards)
  useEffect(() => {
    const loadAllCounts = async () => {
      if (!user?.token) return;

      try {
        console.log('Loading counts via stats API...');
        const response = await meetingAPI.getMeetingStats();
        if (response?.data) {
          const stats = response.data;
          setUpcomingTotal(stats.upcoming || 0);
          setOngoingTotal(stats.ongoing || 0);
          setPendingTotal(stats.pending || 0);
          setCompletedTotal(stats.completed || 0);
          setCancelledTotal(stats.cancelled || 0);
          console.log('Stats loaded successfully');
        }
      } catch (error) {
        console.error('Failed to load counts:', error);
      }
    };

    loadAllCounts();
  }, [user?.token, pageSize]);

  // Load meetings for active tab
  useEffect(() => {
    const loadMeetings = async () => {
      if (!user?.token) {
        console.log('No user token, skipping load');
        return;
      }

      console.log(`Loading meetings for tab: ${activeMeetingTab}`);
      setLoading(true);
      try {
        let result;
        if (activeMeetingTab === 'upcoming') {
          result = await fetchMeetingsByState('upcoming', upcomingPage, debouncedSearchTerm);
          console.log('Upcoming result:', result);
          if (result) {
            setMeetings(result.items || []);
            setUpcomingFilteredTotal(result.total || 0);
            setUpcomingFilteredPages(result.pages || 1);
            console.log(`Set ${result.items?.length || 0} upcoming meetings`);
          }
        } else if (activeMeetingTab === 'ongoing') {
          result = await fetchMeetingsByState('ongoing', ongoingPage, debouncedSearchTerm);
          console.log('Ongoing result:', result);
          if (result) {
            setMeetings(result.items || []);
            setOngoingFilteredTotal(result.total || 0);
            setOngoingFilteredPages(result.pages || 1);
            console.log(`Set ${result.items?.length || 0} ongoing meetings`);
          }
        } else if (activeMeetingTab === 'pending') {
          result = await fetchMeetingsByState('pending', pendingPage, debouncedSearchTerm);
          console.log('Pending result:', result);
          if (result) {
            setMeetings(result.items || []);
            setPendingFilteredTotal(result.total || 0);
            setPendingFilteredPages(result.pages || 1);
            console.log(`Set ${result.items?.length || 0} pending meetings`);
          }
        } else if (activeMeetingTab === 'completed') {
          result = await fetchMeetingsByState('completed', completedPage, debouncedSearchTerm);
          console.log('Completed result:', result);
          if (result) {
            setMeetings(result.items || []);
            setCompletedFilteredTotal(result.total || 0);
            setCompletedFilteredPages(result.pages || 1);
            console.log(`Set ${result.items?.length || 0} completed meetings`);
          }
        } else if (activeMeetingTab === 'cancelled') {
          result = await fetchMeetingsByState('cancelled', cancelledPage, debouncedSearchTerm);
          console.log('Cancelled result:', result);
          if (result) {
            setMeetings(result.items || []);
            setCancelledFilteredTotal(result.total || 0);
            setCancelledFilteredPages(result.pages || 1);
            console.log(`Set ${result.items?.length || 0} cancelled meetings`);
          }
        }
      } catch (error) {
        console.error('Failed to fetch meetings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMeetings();
  }, [user?.token, activeMeetingTab, upcomingPage, ongoingPage, completedPage, cancelledPage, pageSize, debouncedSearchTerm]);

  // Load users once
  useEffect(() => {
    const loadUsers = async () => {
      if (!user?.token) return;

      try {
        const usersResponse = await userAPI.getUsers({ include_vendors: true });
        if (usersResponse?.data) {
          setAllUsers(usersResponse.data);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };

    loadUsers();
  }, [user?.token]);

  const refreshReviewCount = useCallback(async () => {
    if (!user?.token) return;
    try {
      const empCount = await fetchEmployeeReviewCount();
      setEmployeeReviewCount(empCount);
    } catch (error) {
      console.error('Failed to refresh review count:', error);
    }
  }, [user?.token]);

  useEffect(() => {
    refreshReviewCount();
  }, [refreshReviewCount]);

  // Live update review count when task status changes (approve/reject actions)
  useEffect(() => {
    if (!user?.token) return;

    // Listen for task status changes to update review count instantly
    window.addEventListener('taskStatusChanged', refreshReviewCount);

    return () => {
      window.removeEventListener('taskStatusChanged', refreshReviewCount);
    };
  }, [user?.token, refreshReviewCount]);

  // Refresh review count when switching to the review tab
  useEffect(() => {
    if (activeTab === 'review') {
      refreshReviewCount();
    }
  }, [activeTab, refreshReviewCount]);

  // Refresh all counts (for stats cards) and current tab's data
  // Defined before hooks so it can be used in useEffect
  const refreshAllData = useCallback(async () => {
    try {
      // Fetch stats for cards and badges
      const statsResponse = await meetingAPI.getMeetingStats();
      if (statsResponse?.data) {
        const stats = statsResponse.data;
        setUpcomingTotal(stats.upcoming || 0);
        setOngoingTotal(stats.ongoing || 0);
        setPendingTotal(stats.pending || 0);
        setCompletedTotal(stats.completed || 0);
        setCancelledTotal(stats.cancelled || 0);
      }

      // Now fetch the current page for the active tab to show correct data
      let activeTabResult = null;
      if (activeMeetingTab === 'upcoming') {
        activeTabResult = await fetchMeetingsByState('upcoming', upcomingPage, debouncedSearchTerm);
      } else if (activeMeetingTab === 'ongoing') {
        activeTabResult = await fetchMeetingsByState('ongoing', ongoingPage, debouncedSearchTerm);
      } else if (activeMeetingTab === 'pending') {
        activeTabResult = await fetchMeetingsByState('pending', pendingPage, debouncedSearchTerm);
      } else if (activeMeetingTab === 'completed') {
        activeTabResult = await fetchMeetingsByState('completed', completedPage, debouncedSearchTerm);
      } else if (activeMeetingTab === 'cancelled') {
        activeTabResult = await fetchMeetingsByState('cancelled', cancelledPage, debouncedSearchTerm);
      }

      if (activeTabResult) {
        setMeetings(activeTabResult.items || []);
        // Update filtered counts for pagination metadata
        if (activeMeetingTab === 'upcoming') {
          setUpcomingFilteredTotal(activeTabResult.total || 0);
          setUpcomingFilteredPages(activeTabResult.pages || 1);
        } else if (activeMeetingTab === 'ongoing') {
          setOngoingFilteredTotal(activeTabResult.total || 0);
          setOngoingFilteredPages(activeTabResult.pages || 1);
        } else if (activeMeetingTab === 'pending') {
          setPendingFilteredTotal(activeTabResult.total || 0);
          setPendingFilteredPages(activeTabResult.pages || 1);
        } else if (activeMeetingTab === 'completed') {
          setCompletedFilteredTotal(activeTabResult.total || 0);
          setCompletedFilteredPages(activeTabResult.pages || 1);
        } else if (activeMeetingTab === 'cancelled') {
          setCancelledFilteredTotal(activeTabResult.total || 0);
          setCancelledFilteredPages(activeTabResult.pages || 1);
        }
      }
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  }, [activeMeetingTab, upcomingPage, ongoingPage, completedPage, cancelledPage, pageSize, debouncedSearchTerm]);

  // Auto-refresh meetings data every 30 seconds
  useEffect(() => {
    if (!user?.token) return;

    const refreshMeetings = async () => {
      try {
        // Refresh all stats in one call
        const statsResponse = await meetingAPI.getMeetingStats();
        if (statsResponse?.data) {
          const stats = statsResponse.data;
          setUpcomingTotal(stats.upcoming || 0);
          setOngoingTotal(stats.ongoing || 0);
          setPendingTotal(stats.pending || 0);
          setCompletedTotal(stats.completed || 0);
          setCancelledTotal(stats.cancelled || 0);
        }

        // Only refresh the active meeting list to reduce load
        const activeTabResult = await fetchMeetingsByState(
          activeMeetingTab,
          activeMeetingTab === 'upcoming' ? upcomingPage :
            activeMeetingTab === 'ongoing' ? ongoingPage :
              activeMeetingTab === 'pending' ? pendingPage :
                activeMeetingTab === 'completed' ? completedPage : cancelledPage,
          debouncedSearchTerm
        );

        if (activeTabResult) {
          setMeetings(activeTabResult.items || []);
          // Ensure pages and totals are sync'd for the active tab (for pagination)
          if (activeMeetingTab === 'upcoming') {
            setUpcomingFilteredTotal(activeTabResult.total || 0);
            setUpcomingFilteredPages(activeTabResult.pages || 1);
          }
          if (activeMeetingTab === 'ongoing') {
            setOngoingFilteredTotal(activeTabResult.total || 0);
            setOngoingFilteredPages(activeTabResult.pages || 1);
          }
          if (activeMeetingTab === 'pending') {
            setPendingFilteredTotal(activeTabResult.total || 0);
            setPendingFilteredPages(activeTabResult.pages || 1);
          }
          if (activeMeetingTab === 'completed') {
            setCompletedFilteredTotal(activeTabResult.total || 0);
            setCompletedFilteredPages(activeTabResult.pages || 1);
          }
          if (activeMeetingTab === 'cancelled') {
            setCancelledFilteredTotal(activeTabResult.total || 0);
            setCancelledFilteredPages(activeTabResult.pages || 1);
          }
        }
      } catch (error) {
        console.error('Failed to refresh meetings:', error);
      }
    };

    // Set up interval to refresh every 30 seconds
    const intervalId = setInterval(refreshMeetings, 60000);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [user?.token, activeMeetingTab, upcomingPage, ongoingPage, completedPage, cancelledPage, pageSize, debouncedSearchTerm]);

  // Listen for task status changes and refresh
  useEffect(() => {
    if (!user?.token) return;

    const handleTaskStatusChange = async () => {
      try {
        // Refresh all stats
        const statsResponse = await meetingAPI.getMeetingStats();
        if (statsResponse?.data) {
          const stats = statsResponse.data;
          setUpcomingTotal(stats.upcoming || 0);
          setOngoingTotal(stats.ongoing || 0);
          setPendingTotal(stats.pending || 0);
          setCompletedTotal(stats.completed || 0);
          setCancelledTotal(stats.cancelled || 0);
        }

        // Refresh the active meeting list
        const activeTabResult = await fetchMeetingsByState(
          activeMeetingTab,
          activeMeetingTab === 'upcoming' ? upcomingPage :
            activeMeetingTab === 'ongoing' ? ongoingPage :
              activeMeetingTab === 'pending' ? pendingPage :
                activeMeetingTab === 'completed' ? completedPage : cancelledPage,
          debouncedSearchTerm
        );

        if (activeTabResult) {
          setMeetings(activeTabResult.items || []);

          // Update filtered totals and pages for the active tab to keep pagination in sync
          if (activeMeetingTab === 'upcoming') {
            setUpcomingFilteredTotal(activeTabResult.total || 0);
            setUpcomingFilteredPages(activeTabResult.pages || 1);
          }
          if (activeMeetingTab === 'ongoing') {
            setOngoingFilteredTotal(activeTabResult.total || 0);
            setOngoingFilteredPages(activeTabResult.pages || 1);
          }
          if (activeMeetingTab === 'pending') {
            setPendingFilteredTotal(activeTabResult.total || 0);
            setPendingFilteredPages(activeTabResult.pages || 1);
          }
          if (activeMeetingTab === 'completed') {
            setCompletedFilteredTotal(activeTabResult.total || 0);
            setCompletedFilteredPages(activeTabResult.pages || 1);
          }
          if (activeMeetingTab === 'cancelled') {
            setCancelledFilteredTotal(activeTabResult.total || 0);
            setCancelledFilteredPages(activeTabResult.pages || 1);
          }
        }
      } catch (error) {
        console.error('Failed to refresh data:', error);
      }
    };



    // Listen for meeting created event (backup in case direct refresh doesn't work)
    const handleMeetingCreated = async () => {
      // Add a small delay to ensure backend has processed the new meeting
      setTimeout(async () => {
        await refreshAllData();
      }, 500);
    };

    window.addEventListener('taskStatusChanged', handleTaskStatusChange);
    window.addEventListener('meetingCreated', handleMeetingCreated);

    return () => {
      window.removeEventListener('taskStatusChanged', handleTaskStatusChange);
      window.removeEventListener('meetingCreated', handleMeetingCreated);
    };
  }, [user?.token, activeMeetingTab, upcomingPage, ongoingPage, completedPage, cancelledPage, pageSize, refreshAllData, debouncedSearchTerm]);




  const handleCreateMeeting = async (meetingData, skipNotification = false) => {
    try {
      const response = await meetingAPI.createMeeting(meetingData, skipNotification);
      showToast.success('Meeting created successfully!');
      setIsCreateModalOpen(false);
      // Add a small delay to ensure backend has processed the new meeting
      // Then refresh all data (counts + current tab)
      setTimeout(async () => {
        await refreshAllData();
      }, 500);

      // Return response for agenda document upload
      return response;
    } catch (error) {
      // Error handling is done in the modal
      throw error;
    }
  };

  const handleMOMModalOpen = (meeting) => {
    setSelectedMeeting(meeting);
    setIsMOMModalOpen(true);
  };

  const handleActionPointsViewModalOpen = (meeting) => {
    setSelectedMeeting(meeting);
    setIsActionPointsViewModalOpen(true);
  };

  const handleMOMModalSuccess = async () => {
    // Refresh meetings data to show updated action points
    await refreshAllData();
  };

  const handleEditMeeting = (meeting) => {
    setMeetingToEdit(meeting);
    setIsEditMeetingModalOpen(true);
  };

  const handleUpdateMeeting = async (meetingId, meetingData, skipNotification = false) => {
    try {
      await meetingAPI.updateMeeting(meetingId, meetingData, skipNotification);
      showToast.success('Meeting updated successfully');
      setIsEditMeetingModalOpen(false);
      setMeetingToEdit(null);
      // Refresh meetings data
      await refreshAllData();
    } catch (error) {
      handleAPIError(error, 'Failed to update meeting', true);
    }
  };

  const handleCancelClick = (meeting) => {
    setMeetingToCancel(meeting);
    // Check if this is a recurring meeting using the is_recurring field from API
    const isRecurring = meeting.is_recurring || (meeting.recurrence && meeting.recurrence !== 'none');
    if (isRecurring) {
      setIsRecurringCancelDialogOpen(true);
    } else {
      setIsCancelDialogOpen(true);
    }
  };

  const handleCancelMeeting = async () => {
    if (!meetingToCancel) return;

    try {
      await meetingAPI.cancelMeeting(meetingToCancel.id, { cancelAll: true });
      showToast.success('Meeting cancelled successfully');
      setIsCancelDialogOpen(false);
      setMeetingToCancel(null);
      await refreshAllData();
    } catch (error) {
      handleAPIError(error, 'Failed to cancel meeting', true);
      setIsCancelDialogOpen(false);
      setMeetingToCancel(null);
    }
  };

  const handleRecurringCancelConfirm = async (cancelAll, occurrenceDate) => {
    if (!meetingToCancel) return;

    setIsCancelLoading(true);
    try {
      await meetingAPI.cancelMeeting(meetingToCancel.id, {
        cancelAll,
        occurrenceDate: cancelAll ? null : occurrenceDate
      });

      if (cancelAll) {
        showToast.success('All meeting occurrences cancelled successfully');
      } else {
        showToast.success('Meeting occurrence cancelled successfully');
      }

      setIsRecurringCancelDialogOpen(false);
      setMeetingToCancel(null);
      await refreshAllData();
    } catch (error) {
      handleAPIError(error, 'Failed to cancel meeting', true);
    } finally {
      setIsCancelLoading(false);
    }
  };

  // Handle page size change - reset all tab pages to 1
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setUpcomingPage(1);
    setOngoingPage(1);
    setPendingPage(1);
    setCompletedPage(1);
    setCancelledPage(1);
  };

  // Utility functions for table display
  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const truncateText = (text, maxLength = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatStatus = (status) => {
    if (status === 'completed') return 'Completed';
    if (status === 'approved') return 'Completed'; // Backward compatibility
    if (!status) return '';
    return status
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'status-completed border-0 hover:!bg-green-100';
      case 'upcoming': return 'bg-blue-100 text-blue-800 border-0 hover:!bg-blue-100';
      case 'ongoing': return 'bg-yellow-100 text-yellow-800 border-0 hover:!bg-yellow-100';
      case 'pending': return 'bg-amber-100 text-amber-800 border-0 hover:!bg-amber-100';
      case 'cancelled': return 'bg-red-100 text-red-800 border-0 hover:!bg-red-100';
      default: return 'bg-gray-100 text-gray-800 border-0 hover:!bg-gray-100';
    }
  };

  return (
    <Layout>
      <div className="space-y-1 fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex  items-start  gap-2">
          <div className="block lg:hidden w-full">
            <div className="flex flex-col  gap-2">
              <h1 className="text-md font-bold text-gray-900" data-testid="employee-dashboard-title">
                Welcome back, {user.name}
              </h1>
              {activeTab === 'meetings' && (
                <div className="block sm:hidden">
                  <Button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="btn-gold"
                    data-testid="create-meeting-btn-mobile"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Meeting
                  </Button>
                </div>
              )}
            </div>
            {/* <p className="text-gray-600 mt-1">Track your meetings, tasks, and stay productive</p> */}
          </div>

        </div>

        {/* Stats Cards - Only show on Meetings tab */}
        {activeTab === 'meetings' && (
          <div className="grid grid-cols-2 md:grid-cols-2   lg:grid-cols-4 gap-2 sm:gap-6">
            <Card className="glass-card border-0 h-fit hover:shadow-lg transition-all">
              <CardContent className="p-4">
                <div className="flex  justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Meetings</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="total-meetings-count">
                      {upcomingTotal + ongoingTotal + pendingTotal + completedTotal + cancelledTotal}
                    </p>
                  </div>
                  <div className="w-6 sm:w-8 sm:h-8 h-6 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-3 sm:w-4 sm:h-4 h-3 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card h-fit border-0 hover:shadow-lg transition-all">
              <CardContent className="p-4">
                <div className="flex gap-1  justify-between">
                  <div>
                    <p className="text-sm font-medium text-wrap text-gray-600">Upcoming Meets</p>
                    <p className="text-xl sm:text-2xl  font-bold text-gray-900" data-testid="upcoming-meetings-count">
                      {upcomingTotal}
                    </p>
                  </div>
                  <div className="w-6 sm:w-8 sm:h-8 h-6 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Calendar className="w-3 sm:w-4 sm:h-4 h-3 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card h-fit border-0 hover:shadow-lg transition-all">
              <CardContent className="p-4">
                <div className="flex  justify-between">
                  <div>
                    <p className="text-sm font-medium text-wrap text-gray-600">Ongoing Meets</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="ongoing-meetings-count">
                      {ongoingTotal}
                    </p>
                  </div>
                  <div className="w-6 sm:w-8 sm:h-8 h-6 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-3 sm:w-4 sm:h-4 h-3 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card h-fit border-0 hover:shadow-lg transition-all">
              <CardContent className="p-4">
                <div className="flex  justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Completed Meets</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900" data-testid="completed-meetings-count">
                      {completedTotal}
                    </p>
                  </div>
                  <div className="w-6 sm:w-8 sm:h-8 h-6 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckSquare2 className="w-3 sm:w-4 sm:h-4 h-3 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-3"
        >
          <TabsList className=" w-full grid-cols-2 h-auto sm:h-auto flex flex-wrap sm:grid lg:w-auto lg:grid-cols-4">
            <TabsTrigger value="meetings" data-testid="meetings-tab">Meetings</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="calendar-tab">Calendar</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tasks-tab">My Tasks</TabsTrigger>

            <TabsTrigger value="review" data-testid="review-tab" className="flex items-center gap-2">Review <span className="text-xs  px-2 py-1  bg-red-500 text-white rounded-md">{employeeReviewCount}</span></TabsTrigger>
          </TabsList>

          <TabsContent value="meetings" className="space-y-6">
            {/* Meeting Sub-tabs */}
            <Tabs
              defaultValue="upcoming"
              value={activeMeetingTab}
              onValueChange={setActiveMeetingTab}
              className="space-y-6"
            >
              <TabsList className="flex flex-col lg:flex-row items-center justify-between w-full bg-transparent border-b-0 gap-4 mb-4 h-auto">
                {/* Left placeholder — matches right panel width for perfect centering of tabs */}
                <div className="hidden lg:block" style={{ minWidth: '340px' }}></div>
                
                <div className="flex justify-center w-full lg:w-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-1 sm:gap-5 bg-gray-100/50 p-2  rounded-lg w-full sm:w-auto">
                    <TabsTrigger  value="upcoming" data-testid="upcoming-meetings-tab">Upcoming ({upcomingTotal})</TabsTrigger>
                    <TabsTrigger value="ongoing" data-testid="ongoing-meetings-tab">Ongoing ({ongoingTotal})</TabsTrigger>
                    <TabsTrigger value="pending" data-testid="pending-meetings-tab">Pending ({pendingTotal})</TabsTrigger>
                    <TabsTrigger value="completed" data-testid="completed-meetings-tab">Completed ({completedTotal})</TabsTrigger>
                    <TabsTrigger value="cancelled" data-testid="cancelled-meetings-tab">Cancelled ({cancelledTotal})</TabsTrigger>
                  </div>
                </div>

                {/* Right: Search + Export always side by side */}
                <div className="w-full lg:w-auto flex flex-row gap-2 items-center" style={{ minWidth: 0 }}>
                  {/* Search bar */}
                  <div
                    className="flex-1 min-w-0"
                    style={{
                      background: 'linear-gradient(135deg, var(--primary-gold), var(--hover-gold))',
                      padding: '1px',
                      borderRadius: '8px'
                    }}
                  >
                    <input
                      className="p-1.5 px-3 rounded-lg outline-none w-full"
                      placeholder="Search..."
                      style={{ color: 'black' }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Export button */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        className="flex-shrink-0 flex py-[7px] px-3 justify-center items-center gap-1.5 cursor-pointer text-xs rounded-lg transition-all hover:opacity-90 active:scale-[0.98] text-white font-medium whitespace-nowrap"
                        style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--hover-gold))' }}
                      >
                        <Download className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="hidden sm:inline">Export</span>
                        <span className="sm:hidden">Export</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[90vw] sm:w-72 p-0 overflow-hidden shadow-2xl border border-gray-100 z-[9999]"
                      align="end"
                      side="bottom"
                      sideOffset={8}
                    >
                      <div className="p-3 sm:p-4 space-y-2">
                        <h4 className="font-semibold text-sm sm:text-base text-blue-900">Export Date Range</h4>
                        <p className="text-[10px] sm:text-[11px] text-gray-500 leading-tight">Select the period you want to export meetings for.</p>
                        <p className="text-[9px] sm:text-[10px] text-red-500 font-medium italic">* You can only get the data of max 90 days</p>
                        <div className="flex justify-center pt-1">
                          <CalendarPicker
                            mode="range"
                            defaultMonth={exportDateRange?.from}
                            selected={exportDateRange}
                            onSelect={setExportDateRange}
                            numberOfMonths={1}
                            className="rounded-md border p-0.5 sm:p-1"
                          />
                        </div>
                      </div>
                      <div className="bg-blue-50/80 p-3 flex justify-between items-center border-t border-blue-100">
                        <div className="flex flex-col">
                          <span className="text-[9px] text-blue-600 uppercase font-bold tracking-wider">Selection</span>
                          <span className="text-xs font-bold text-blue-900">
                            {exportDateRange?.from ? (
                              exportDateRange.to ? (
                                `${format(exportDateRange.from, 'MMM dd')} - ${format(exportDateRange.to, 'MMM dd')}`
                              ) : (
                                format(exportDateRange.from, 'MMM dd, yyyy')
                              )
                            ) : (
                              'Entire History'
                            )}
                          </span>
                        </div>
                        <button
                          onClick={handleExportMeetings}
                          className="flex gap-2 px-3 py-2 cursor-pointer text-[11px] font-bold rounded-md items-center transition-all text-white whitespace-nowrap"
                          style={{ background: '#f59e0b', cursor: loadingExport ? 'not-allowed' : 'pointer', opacity: loadingExport ? 0.7 : 1 }}
                          disabled={loadingExport}
                        >
                          {loadingExport ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                          Download XLSX
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </TabsList>

              <TabsContent value="upcoming" className="space-y-6">
                <Card className="glass-card border-0 ">

                  <CardContent className='mt-2'>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="loading-shimmer h-32 w-full rounded-lg"></div>
                      </div>
                    ) : meetings.length === 0 ? (
                      <div className="text-center py-8" data-testid="no-upcoming-meetings-message">
                        <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No upcoming meetings</p>
                        <p className="text-sm text-gray-400 mt-1">Create a new meeting to get started</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block rounded-lg border overflow-x-auto">
                          <div className="max-h-[calc(100vh-370px)] overflow-auto">
                            <div className="relative w-full overflow-visible">
                              <table className="w-full overflow-x-auto caption-bottom text-sm">
                                <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                                  <tr className='bg-gray-100 border-b'>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr.<br></br>No.</th>
                                    <th className="h-10 px-2 text-center   align-middle font-medium text-muted-foreground">Meeting</th>
                                    {/* <th className="h-10 px-2 text-center font-medium text-muted-foreground">Agenda</th> */}
                                    {/* <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Recurrence</th> */}
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Date</th>
                                    <th className="h-10 px-2 text-center  font-medium  text-muted-foreground">Time</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Organizer</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Participants</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Status</th>

                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Location</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Meeting Type</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                  {meetings.map((meeting, index) => {
                                    const actionPointsCount = meeting.action_points_total || 0;
                                    const completedActionPoints = meeting.action_points_completed || 0;

                                    return (
                                      <tr key={meeting.id} className="border-b transition-colors hover:bg-gray-50">
                                        <td className="p-2 text-center align-middle">
                                          {(upcomingPage - 1) * pageSize + index + 1}
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="w-32 font-medium text-gray-900 cursor-pointer mx-auto">
                                                {truncateText(meeting.title, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="w-32 font-medium text-gray-900 mx-auto">{meeting.title || '-'}</p>
                                          )}
                                        </td>
                                        {/* <td className="p-2 text-center align-middle">
                                          {meeting.agenda && meeting.agenda.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.agenda}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-medium cursor-pointer mx-auto">
                                                {truncateText(meeting.agenda, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-medium">{meeting.agenda || '-'}</p>
                                          )}
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{formatRecurrence(meeting.recurrence)}</span>
                                        </td> */}
                                        <td className="p-2 text-center align-middle">
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium">{meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}</p>
                                          </div>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{meeting.organizer?.full_name}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <ResponsiveTooltip
                                            content={
                                              <div className="space-y-1">
                                                {meeting.organizer && (
                                                  <div className="text-xs">
                                                    <span className="font-semibold">Organizer: </span>
                                                    {meeting.organizer?.full_name || meeting.organizer?.name || 'Unknown'}
                                                  </div>
                                                )}
                                                {meeting.participants && meeting.participants.length > 0 && (
                                                  <div className="space-y-0.5">
                                                    <div className="text-xs font-semibold">Participants:</div>
                                                    {meeting.participants.map((participant, idx) => (
                                                      <div key={idx} className="text-xs">
                                                        {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            }
                                          >
                                            <div className="flex items-center justify-center gap-1 cursor-pointer">
                                              <Users className="w-4 h-4 text-gray-400" />
                                              <span className="text-sm">{meeting.participant_count}</span>
                                            </div>
                                          </ResponsiveTooltip>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs text-nowrap`}>
                                            {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                          </Badge>
                                        </td>

                                        <td className='p-2 align-middle text-center'>
                                          <div className="flex items-center justify-center gap-1">
                                            {meeting.location && meeting.location.length > 20 ? (
                                              <ResponsiveTooltip
                                                content={<p className="text-sm whitespace-pre-wrap">{meeting.location}</p>}
                                                className="max-w-md p-3"
                                              >
                                                <span className="text-xs cursor-pointer mx-auto">
                                                  {truncateText(meeting.location, 20)}
                                                </span>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs mx-auto">{meeting.location || '-'}</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className='p-2 align-middle text-center'>
                                          <Badge variant="outline" className="text-[10px] h-5 px-1 py-0 capitalize">
                                            {meeting.meeting_type || '-'}
                                          </Badge>
                                        </td>
                                        {/* <td className="p-2 align-middle">
                                          <div className="flex items-center gap-2 justify-center">
                                            {actionPointsCount > 0 ? (
                                              <ResponsiveTooltip content={<p className="text-xs">View action points ({actionPointsCount})</p>}>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={() => handleActionPointsViewModalOpen(meeting)}
                                                  className="h-8 w-8 p-0 hover:bg-blue-50"
                                                >
                                                  <Eye className="w-4 h-4 text-blue-600" />
                                                </Button>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs text-gray-400">None</span>
                                            )}
                                          </div>
                                        </td> */}
                                        <td className="p-2 text-center align-middle">
                                          <div className="flex items-center justify-center gap-2">
                                            
                                            <ResponsiveTooltip content={<p className="text-xs">Edit Meeting</p>}>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleEditMeeting(meeting)}
                                                className="h-8 w-8 p-0 hover:bg-blue-50"
                                                disabled={!meeting.can_edit || meeting.organizer?.id !== user?.id}
                                              >
                                                <Edit className={`w-4 h-4 ${!meeting.can_edit || meeting.organizer?.id !== user?.id ? 'text-gray-400' : 'text-blue-600'}`} />
                                              </Button>
                                            </ResponsiveTooltip>
                                            {(user?.role === 'admin' || meeting.organizer?.id === user?.id) && (
                                              <ResponsiveTooltip content={<p className="text-xs">Cancel Meeting</p>}>
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  onClick={() => handleCancelClick(meeting)}
                                                  className="h-8 w-8 p-0 hover:bg-red-50"
                                                >
                                                  <Trash className="w-4 h-4 text-red-600" />
                                                </Button>
                                              </ResponsiveTooltip>
                                            )}
                                           
                                          </div>
                                        </td>

                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        {/* Mobile Accordion View */}
                        <div className="md:hidden">
                          <Accordion type="single" collapsible className="w-full space-y-2">
                            {meetings.map((meeting, index) => {
                              const actionPointsCount = meeting.action_points_total || 0;
                              const completedActionPoints = meeting.action_points_completed || 0;

                              return (
                                <AccordionItem key={meeting.id} value={`item-${meeting.id}`} className="border rounded-lg px-4 bg-white">
                                  <AccordionTrigger className="hover:no-underline py-4">
                                    <div className="flex items-center justify-between w-full pr-4">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                                          {(upcomingPage - 1) * pageSize + index + 1}.
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-semibold text-gray-900 cursor-pointer">
                                                {truncateText(meeting.title, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                              {meeting.title || '-'}
                                            </p>
                                          )}
                                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                              {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                            </Badge>
                                            <Badge variant="outline" className="text-[10px] h-5 px-1 py-0 capitalize border-blue-200 bg-blue-50 text-blue-700">
                                              {meeting.meeting_type || '-'}
                                            </Badge>
                                            <span className="text-xs text-gray-500">
                                              {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd') : '-'}
                                            </span>
                                            <div className="flex items-center gap-1 ml-auto">
                                              {meeting.location && meeting.location.length > 20 ? (
                                                <ResponsiveTooltip
                                                  content={<p className="text-sm whitespace-pre-wrap">{meeting.location}</p>}
                                                  className="max-w-md p-3"
                                                >
                                                  <span className="text-xs text-gray-500 truncate max-w-[80px] cursor-pointer">
                                                    {truncateText(meeting.location, 20)}
                                                  </span>
                                                </ResponsiveTooltip>
                                              ) : (
                                                <span className="text-xs text-gray-500 truncate max-w-[80px]">{meeting.location || '-'}</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="pb-4 pt-0">
                                    <div className="space-y-4  gap-2 pt-2 border-t">
                                      {/* Meeting Title */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <Popover>
                                              <PopoverTrigger asChild>
                                                <button className="text-sm text-gray-900 text-left hover:underline cursor-pointer">
                                                  {truncateText(meeting.title, 20)}
                                                </button>
                                              </PopoverTrigger>
                                              <PopoverContent className="w-72 p-3" align="start">
                                                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                                  {meeting.title}
                                                </div>
                                              </PopoverContent>
                                            </Popover>
                                          ) : (
                                            <p className="text-sm text-gray-900">{meeting.title || '-'}</p>
                                          )}
                                        </div>


                                        {/* Recurrence */}
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Recurrence</p>
                                          <p className="text-sm text-gray-900">{formatRecurrence(meeting.recurrence)}</p>
                                        </div>
                                      </div>
                                      {/* Agenda */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Agenda</p>
                                          {meeting.agenda && meeting.agenda.length > 20 ? (
                                            <Popover>
                                              <PopoverTrigger asChild>
                                                <button className="text-sm text-gray-900 text-left hover:underline cursor-pointer">
                                                  {truncateText(meeting.agenda, 20)}
                                                </button>
                                              </PopoverTrigger>
                                              <PopoverContent className="w-72 p-3" align="start">
                                                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                                  {meeting.agenda}
                                                </div>
                                              </PopoverContent>
                                            </Popover>
                                          ) : (
                                            <p className="text-sm text-gray-900">{meeting.agenda || '-'}</p>
                                          )}

                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1 flex flex-wrap gap-1">Participants  <Users className="w-4 h-4 text-gray-400" /></p>
                                          <div className="flex items-center gap-2">

                                            <span className="text-sm text-gray-900">{meeting.participant_count || 0}</span>
                                            {meeting.participants && meeting.participants.length > 0 && (
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <button className="text-xs text-blue-600 hover:underline cursor-pointer">
                                                    (View list)
                                                  </button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-64 p-3" align="start">
                                                  <div className="space-y-2">

                                                    {meeting.participants.length > 0 && (
                                                      <div className="space-y-1">
                                                        <div className="text-xs font-semibold mb-1">Participants:</div>
                                                        {meeting.participants.map((participant, idx) => (
                                                          <div key={idx} className="text-xs pl-2">
                                                            {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                </PopoverContent>
                                              </Popover>
                                            )}
                                          </div>
                                        </div>

                                      </div>


                                      <div className='flex flex-wrap justify-between gap-2'>
                                        {/* Date */}
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Date</p>
                                          <p className="text-sm  text-gray-900">
                                            {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}
                                          </p>
                                        </div>

                                        {/* Time */}
                                        <div className='text-center'>
                                          <p className="text-xs font-medium text-gray-500 mb-1 flex gap-1 justify-center items-center">Time <Clock className="w-3 h-3 text-gray-400" /></p>
                                          <p className="text-xs text-gray-900">
                                            {formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}
                                          </p>
                                        </div>

                                      </div>

                                      {/* Organizer */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Organizer</p>
                                          <p className="text-sm text-gray-900">{meeting.organizer?.full_name || '-'}</p>
                                        </div>

                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Actions</p>
                                          <div className="flex items-center gap-2">
                                            <ResponsiveTooltip content={<p className="text-xs">Edit Meeting</p>}>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleEditMeeting(meeting)}
                                                className="h-8 w-8 p-0 hover:bg-blue-50"
                                                disabled={!meeting.can_edit || meeting.organizer?.id !== user?.id}
                                              >
                                                <Edit className={`w-4 h-4 ${!meeting.can_edit || meeting.organizer?.id !== user?.id ? 'text-gray-400' : 'text-blue-600'}`} />
                                              </Button>
                                            </ResponsiveTooltip>
                                            {meeting.organizer?.id === user?.id && (
                                              <ResponsiveTooltip content={<p className="text-xs">Cancel Meeting</p>}>
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  onClick={() => handleCancelClick(meeting)}
                                                  className="h-8 w-8 p-0 hover:bg-red-50"
                                                >
                                                  <Trash className="w-4 h-4 text-red-600" />
                                                </Button>
                                              </ResponsiveTooltip>
                                            )}
                                          </div>
                                        </div>


                                      </div>
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              );
                            })}
                          </Accordion>
                        </div>

                        {/* Pagination - Always show if there's data */}
                        {upcomingFilteredTotal > 0 && (
                          <div className="mt-6 border-t pt-4">
                            <Pagination
                              page={upcomingPage}
                              pages={upcomingFilteredPages}
                              total={upcomingFilteredTotal}
                              pageSize={pageSize}
                              hasNext={upcomingPage < upcomingFilteredPages}
                              hasPrev={upcomingPage > 1}
                              onPageChange={setUpcomingPage}
                              onPageSizeChange={handlePageSizeChange}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="ongoing" className="space-y-6">
                <Card className="glass-card border-0">

                  <CardContent className='mt-2'>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="loading-shimmer h-32 w-full rounded-lg"></div>
                      </div>
                    ) : meetings.length === 0 ? (
                      <div className="text-center py-8" data-testid="no-ongoing-meetings-message">
                        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No meetings in progress</p>
                        <p className="text-sm text-gray-400 mt-1">Check back during meeting times</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block rounded-lg border overflow-x-auto">
                          <div className="max-h-[calc(100vh-370px)] overflow-auto">
                            <div className="relative w-full overflow-visible">
                              <table className="w-full overflow-x-auto caption-bottom text-sm">
                                <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                                  <tr className='bg-gray-100 border-b'>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr.<br></br>No.</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Meeting</th>
                                    {/* <th className="h-10 px-2 text-left font-medium text-muted-foreground">Agenda</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Recurrence</th> */}
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Date</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Time</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Organizer</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Participants</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Status</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Action Points</th>

                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                  {meetings.map((meeting, index) => {
                                    const actionPointsCount = meeting.action_points_total || 0;
                                    const completedActionPoints = meeting.action_points_completed || 0;

                                    return (
                                      <tr key={meeting.id} className="border-b transition-colors hover:bg-gray-50">
                                        <td className="p-2 text-center align-middle">
                                          {(ongoingPage - 1) * pageSize + index + 1}
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="w-32 font-medium text-gray-900 cursor-pointer mx-auto">
                                                {truncateText(meeting.title, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="w-32 font-medium text-gray-900 mx-auto">{meeting.title || '-'}</p>
                                          )}
                                        </td>
                                        {/* <td className="p-2 align-middle">
                                          {meeting.agenda && meeting.agenda.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.agenda}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-medium cursor-pointer">
                                                {truncateText(meeting.agenda, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-medium">{meeting.agenda || '-'}</p>
                                          )}
                                        </td>
                                        <td className="p-2 align-middle">
                                          <span className="text-sm font-medium">{formatRecurrence(meeting.recurrence)}</span>
                                        </td> */}
                                        <td className="p-2 text-center align-middle">
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium">{meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}</p>
                                          </div>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{meeting.organizer?.full_name}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <ResponsiveTooltip
                                            content={
                                              <div className="space-y-1">
                                                {meeting.organizer && (
                                                  <div className="text-xs">
                                                    <span className="font-semibold">Organizer: </span>
                                                    {meeting.organizer?.full_name || meeting.organizer?.name || 'Unknown'}
                                                  </div>
                                                )}
                                                {meeting.participants && meeting.participants.length > 0 && (
                                                  <div className="space-y-0.5">
                                                    <div className="text-xs font-semibold">Participants:</div>
                                                    {meeting.participants.map((participant, idx) => (
                                                      <div key={idx} className="text-xs">
                                                        {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            }
                                          >
                                            <div className="flex items-center justify-center gap-1 cursor-pointer">
                                              <Users className="w-4 h-4 text-gray-400" />
                                              <span className="text-sm">{meeting.participant_count}</span>
                                            </div>
                                          </ResponsiveTooltip>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                            {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                          </Badge>
                                        </td>
                                        <td className="p-2 align-middle">
                                          <div className="flex items-center gap-2 justify-center">
                                            {actionPointsCount > 0 ? (
                                              <ResponsiveTooltip content={<p className="text-xs">View action points ({actionPointsCount})</p>}>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={() => handleActionPointsViewModalOpen(meeting)}
                                                  className="h-8 w-8 p-0 hover:bg-blue-50"
                                                >
                                                  <Eye className="w-4 h-4 text-blue-600" />
                                                </Button>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs text-gray-400">None</span>
                                            )}
                                            {(meeting.organizer?.id === user?.id || user?.role === 'admin' || meeting.participants?.some(p => p.id === user?.id)) && meeting.can_add_mom && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleMOMModalOpen(meeting)}
                                                className="text-xs h-8 px-3"
                                              >
                                                <FileText className="w-4 h-4 mr-2" />
                                                MOM
                                              </Button>
                                            )}
                                          </div>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <ResponsiveTooltip content={<p className="text-xs">Edit Meeting</p>}>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => handleEditMeeting(meeting)}
                                              className="h-8 w-8 p-0 hover:bg-blue-50"
                                              disabled={!meeting.can_edit || meeting.organizer?.id !== user?.id}
                                            >
                                              <Edit className={`w-4 h-4 ${!meeting.can_edit || meeting.organizer?.id !== user?.id ? 'text-gray-400' : 'text-blue-600'}`} />
                                            </Button>
                                          </ResponsiveTooltip>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        {/* Mobile Accordion View */}
                        <div className="md:hidden">
                          <Accordion type="single" collapsible className="w-full space-y-2">
                            {meetings.map((meeting, index) => {
                              const actionPointsCount = meeting.action_points_total || 0;
                              const completedActionPoints = meeting.action_points_completed || 0;

                              return (
                                <AccordionItem key={meeting.id} value={`item-${meeting.id}`} className="border rounded-lg px-4 bg-white">
                                  <AccordionTrigger className="hover:no-underline py-4">
                                    <div className="flex items-center justify-between w-full pr-4">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                                          {(ongoingPage - 1) * pageSize + index + 1}.
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-semibold text-gray-900 truncate cursor-pointer">
                                                {truncateText(meeting.title, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                              {meeting.title || '-'}
                                            </p>
                                          )}
                                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                              {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                            </Badge>
                                            <span className="text-xs text-gray-500">
                                              {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd') : '-'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="pb-4 pt-0">
                                    <div className="space-y-4  gap-2 pt-2 border-t">
                                      {/* Meeting Title */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                                          <p className="text-sm   text-gray-900">{meeting.title || '-'}</p>
                                        </div>


                                        {/* Recurrence */}
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Recurrence</p>
                                          <p className="text-sm text-gray-900">{formatRecurrence(meeting.recurrence)}</p>
                                        </div>
                                      </div>
                                      {/* Agenda */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Agenda</p>
                                          {meeting.agenda && meeting.agenda.length > 10 ? (
                                            <Popover>
                                              <PopoverTrigger asChild>
                                                <button className="text-sm text-gray-900 text-left hover:underline cursor-pointer">
                                                  {truncateText(meeting.agenda, 10)}
                                                </button>
                                              </PopoverTrigger>
                                              <PopoverContent className="w-72 p-3" align="start">
                                                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                                  {meeting.agenda}
                                                </div>
                                              </PopoverContent>
                                            </Popover>
                                          ) : (
                                            <p className="text-sm text-gray-900">{meeting.agenda || '-'}</p>
                                          )}

                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1 flex flex-wrap gap-1">Participants  <Users className="w-4 h-4 text-gray-400" /></p>
                                          <div className="flex items-center gap-2">

                                            <span className="text-sm text-gray-900">{meeting.participant_count || 0}</span>
                                            {meeting.participants && meeting.participants.length > 0 && (
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <button className="text-xs text-blue-600 hover:underline cursor-pointer">
                                                    (View list)
                                                  </button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-64 p-3" align="start">
                                                  <div className="space-y-2">

                                                    {meeting.participants.length > 0 && (
                                                      <div className="space-y-1">
                                                        <div className="text-xs font-semibold mb-1">Participants:</div>
                                                        {meeting.participants.map((participant, idx) => (
                                                          <div key={idx} className="text-xs pl-2">
                                                            {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                </PopoverContent>
                                              </Popover>
                                            )}
                                          </div>
                                        </div>

                                      </div>


                                      <div className='flex flex-wrap justify-between gap-2'>
                                        {/* Date */}
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Date</p>
                                          <p className="text-sm  text-gray-900">
                                            {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}
                                          </p>
                                        </div>

                                        {/* Time */}
                                        <div className='text-center'>
                                          <p className="text-xs font-medium text-gray-500 mb-1 flex gap-1 justify-center items-center">Time <Clock className="w-3 h-3 text-gray-400" /></p>
                                          <p className="text-xs text-gray-900">
                                            {formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}
                                          </p>
                                        </div>

                                      </div>

                                      {/* Organizer */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Organizer</p>
                                          <p className="text-sm text-gray-900">{meeting.organizer?.full_name || '-'}</p>
                                        </div>

                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Action Points</p>
                                          <div className="flex items-center gap-2">
                                            {actionPointsCount > 0 ? (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleActionPointsViewModalOpen(meeting)}
                                                className="h-8   hover:bg-blue-50 text-blue-600"
                                              >
                                                <span className="text-xs flex gap-1">
                                                  <Eye className="w-4 h-4 " />

                                                  ({actionPointsCount})
                                                </span>
                                              </Button>
                                            ) : (
                                              <span className="text-xs text-gray-400">None</span>
                                            )}
                                            {(meeting.organizer?.id === user?.id || user?.role === 'admin' || meeting.participants?.some(p => p.id === user?.id)) && meeting.can_add_mom && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleMOMModalOpen(meeting)}
                                                className="text-xs h-8 px-3"
                                              >
                                                <FileText className="w-4 h-4 mr-2" />
                                                MOM
                                              </Button>
                                            )}
                                          </div>
                                        </div>


                                      </div>

                                      {/* Actions */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Actions</p>
                                          <div className="flex items-center gap-2">
                                            <ResponsiveTooltip content={<p className="text-xs">Edit Meeting</p>}>
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleEditMeeting(meeting)}
                                                className="h-8 w-8 p-0 hover:bg-blue-50"
                                                disabled={!meeting.can_edit || meeting.organizer?.id !== user?.id}
                                              >
                                                <Edit className={`w-4 h-4 ${!meeting.can_edit || meeting.organizer?.id !== user?.id ? 'text-gray-400' : 'text-blue-600'}`} />
                                              </Button>
                                            </ResponsiveTooltip>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              );
                            })}
                          </Accordion>
                        </div>

                        {/* Pagination - Always show if there's data */}
                        {ongoingFilteredTotal > 0 && (
                          <div className="mt-6 border-t pt-4">
                            <Pagination
                              page={ongoingPage}
                              pages={ongoingFilteredPages}
                              total={ongoingFilteredTotal}
                              pageSize={pageSize}
                              hasNext={ongoingPage < ongoingFilteredPages}
                              hasPrev={ongoingPage > 1}
                              onPageChange={setOngoingPage}
                              onPageSizeChange={handlePageSizeChange}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="pending" className="space-y-6">
                <Card className="glass-card border-0">
                  <CardContent className='mt-2'>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="loading-shimmer h-32 w-full rounded-lg"></div>
                      </div>
                    ) : meetings.length === 0 ? (
                      <div className="text-center py-8" data-testid="no-pending-meetings-message">
                        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No pending meetings</p>
                        <p className="text-sm text-gray-400 mt-1">Meetings awaiting MOM will appear here</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block rounded-lg border overflow-x-auto">
                          <div className="max-h-[calc(100vh-370px)] overflow-auto">
                            <div className="relative w-full overflow-visible">
                              <table className="w-full overflow-x-auto caption-bottom text-sm">
                                <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                                  <tr className='bg-gray-100 border-b'>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr.<br></br>No.</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Meeting</th>
                                    {/* <th className="h-10 px-2 text-left font-medium text-muted-foreground">Agenda</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Recurrence</th> */}
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Date</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Time</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Organizer</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Participants</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Status</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Action Points</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Location</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Meeting Type</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                  {meetings.map((meeting, index) => {
                                    const actionPointsCount = meeting.action_points_total || 0;
                                    return (
                                      <tr key={meeting.id} className="border-b transition-colors hover:bg-gray-50">
                                        <td className="p-2 text-center align-middle">
                                          {(pendingPage - 1) * pageSize + index + 1}
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="w-32 font-medium text-gray-900 cursor-pointer mx-auto">
                                                {truncateText(meeting.title, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="w-32 font-medium text-gray-900 mx-auto">{meeting.title || '-'}</p>
                                          )}
                                        </td>
                                        {/* <td className="p-2 align-middle">
                                          {meeting.agenda && meeting.agenda.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.agenda}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-medium cursor-pointer">
                                                {truncateText(meeting.agenda, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-medium">{meeting.agenda || '-'}</p>
                                          )}
                                        </td>
                                        <td className="p-2 align-middle">
                                          <span className="text-sm font-medium">{formatRecurrence(meeting.recurrence)}</span>
                                        </td> */}
                                        <td className="p-2  align-middle">
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium">{meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}</p>
                                          </div>
                                        </td>
                                        <td className="p-2 align-middle">
                                          <span className="text-sm font-medium">{formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}</span>
                                        </td>
                                        <td className="p-2 align-middle">
                                          <span className="text-sm font-medium">{meeting.organizer?.full_name}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <ResponsiveTooltip
                                            content={
                                              <div className="space-y-1">
                                                {meeting.organizer && (
                                                  <div className="text-xs">
                                                    <span className="font-semibold">Organizer: </span>
                                                    {meeting.organizer?.full_name || meeting.organizer?.name || 'Unknown'}
                                                  </div>
                                                )}
                                                {meeting.participants && meeting.participants.length > 0 && (
                                                  <div className="space-y-0.5">
                                                    <div className="text-xs font-semibold">Participants:</div>
                                                    {meeting.participants.map((participant, idx) => (
                                                      <div key={idx} className="text-xs">
                                                        {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            }
                                          >
                                            <div className="flex items-center justify-center gap-1 cursor-pointer">
                                              <Users className="w-4 h-4 text-gray-400" />
                                              <span className="text-sm">{meeting.participant_count}</span>
                                            </div>
                                          </ResponsiveTooltip>
                                        </td>
                                        <td className="p-2 align-middle">
                                          <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                            {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                          </Badge>
                                        </td>
                                        <td className="p-2 align-middle">
                                          <div className="flex items-center gap-2 justify-center">
                                            {actionPointsCount > 0 ? (
                                              <ResponsiveTooltip content={<p className="text-xs">View action points ({actionPointsCount})</p>}>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={() => handleActionPointsViewModalOpen(meeting)}
                                                  className="h-8 w-8 p-0 hover:bg-blue-50"
                                                >
                                                  <Eye className="w-4 h-4 text-blue-600" />
                                                </Button>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs text-gray-400">None</span>
                                            )}
                                            {(meeting.organizer?.id === user?.id || user?.role === 'admin' || meeting.participants?.some(p => p.id === user?.id)) && meeting.can_add_mom && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleMOMModalOpen(meeting)}
                                                className="text-xs h-8 px-3"
                                              >
                                                <FileText className="w-4 h-4 mr-2" />
                                                MOM
                                              </Button>
                                            )}
                                          </div>
                                        </td>
                                        <td className='p-2 align-middle text-center'>
                                          <div className="flex items-center justify-center gap-1">
                                            {meeting.location && meeting.location.length > 20 ? (
                                              <ResponsiveTooltip
                                                content={<p className="text-sm whitespace-pre-wrap">{meeting.location}</p>}
                                                className="max-w-md p-3"
                                              >
                                                <span className="text-xs cursor-pointer">
                                                  {truncateText(meeting.location, 20)}
                                                </span>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs">{meeting.location || '-'}</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className='p-2 align-middle text-center'>
                                          <Badge variant="outline" className="text-[10px] h-5 px-1 py-0 capitalize border-blue-200 bg-blue-50 text-blue-700">
                                            {meeting.meeting_type || '-'}
                                          </Badge>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <ResponsiveTooltip content={<p className="text-xs">Edit Meeting</p>}>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => handleEditMeeting(meeting)}
                                              className="h-8 w-8 p-0 hover:bg-blue-50"
                                              disabled={!meeting.can_edit || meeting.organizer?.id !== user?.id}
                                            >
                                              <Edit className={`w-4 h-4 ${!meeting.can_edit || meeting.organizer?.id !== user?.id ? 'text-gray-400' : 'text-blue-600'}`} />
                                            </Button>
                                          </ResponsiveTooltip>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        {/* Mobile Accordion View */}
                        <div className="md:hidden">
                          <Accordion type="single" collapsible className="w-full space-y-2">
                            {meetings.map((meeting, index) => {
                              const actionPointsCount = meeting.action_points_total || 0;
                              return (
                                <AccordionItem key={meeting.id} value={`item-${meeting.id}`} className="border rounded-lg px-4 bg-white">
                                  <AccordionTrigger className="hover:no-underline py-4">
                                    <div className="flex items-center justify-between w-full pr-4">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                                          {(pendingPage - 1) * pageSize + index + 1}.
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-semibold text-gray-900 truncate cursor-pointer">
                                                {truncateText(meeting.title, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                              {meeting.title || '-'}
                                            </p>
                                          )}
                                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                              {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                            </Badge>
                                            <span className="text-xs text-gray-500">
                                              {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd') : '-'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="pb-4 pt-0">
                                    <div className="space-y-4 gap-2 pt-2 border-t">
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                                          <p className="text-sm text-gray-900">{meeting.title || '-'}</p>
                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Recurrence</p>
                                          <p className="text-sm text-gray-900">{formatRecurrence(meeting.recurrence)}</p>
                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1 text-nowrap">Type</p>
                                          <Badge variant="outline" className="text-[10px] h-5 px-1 py-0 capitalize border-blue-200 bg-blue-50 text-blue-700">
                                            {meeting.meeting_type || '-'}
                                          </Badge>
                                        </div>
                                      </div>
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Agenda</p>
                                          {meeting.agenda && meeting.agenda.length > 20 ? (
                                            <Popover>
                                              <PopoverTrigger asChild>
                                                <button className="text-sm text-gray-900 text-left hover:underline cursor-pointer">
                                                  {truncateText(meeting.agenda, 20)}
                                                </button>
                                              </PopoverTrigger>
                                              <PopoverContent className="w-72 p-3" align="start">
                                                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                                  {meeting.agenda}
                                                </div>
                                              </PopoverContent>
                                            </Popover>
                                          ) : (
                                            <p className="text-sm text-gray-900">{meeting.agenda || '-'}</p>
                                          )}
                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Participants</p>
                                          <span className="text-sm text-gray-900">{meeting.participant_count || 0}</span>
                                        </div>
                                      </div>
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Date</p>
                                          <p className="text-sm text-gray-900">
                                            {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}
                                          </p>
                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Time</p>
                                          <p className="text-sm text-gray-900">
                                            {formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}
                                          </p>
                                        </div>
                                      </div>
                                      <div className='flex flex-wrap justify-between gap-2 border-t pt-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Organizer</p>
                                          <p className="text-sm text-gray-900">{meeting.organizer?.full_name || '-'}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {(meeting.organizer?.id === user?.id || user?.role === 'admin' || meeting.participants?.some(p => p.id === user?.id)) && meeting.can_add_mom && (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => handleMOMModalOpen(meeting)}
                                              className="text-xs h-8 px-3"
                                            >
                                              <FileText className="w-4 h-4 mr-2" />
                                              MOM
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              );
                            })}
                          </Accordion>
                        </div>

                        {/* Pagination */}
                        {pendingFilteredTotal > 0 && (
                          <div className="mt-6 border-t pt-4">
                            <Pagination
                              page={pendingPage}
                              pages={pendingFilteredPages}
                              total={pendingFilteredTotal}
                              pageSize={pageSize}
                              hasNext={pendingPage < pendingFilteredPages}
                              hasPrev={pendingPage > 1}
                              onPageChange={setPendingPage}
                              onPageSizeChange={handlePageSizeChange}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="completed" className="space-y-6">
                <Card className="glass-card border-0 ">

                  <CardContent className='mt-2'>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="loading-shimmer h-32 w-full rounded-lg"></div>
                      </div>
                    ) : meetings.length === 0 ? (
                      <div className="text-center py-8" data-testid="no-completed-meetings-message">
                        <CheckSquare2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No completed meetings yet</p>
                        <p className="text-sm text-gray-400 mt-1">Completed meetings will appear here</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block rounded-lg border overflow-x-auto">
                          <div className="max-h-[calc(100vh-370px)] overflow-auto">
                            <div className="relative w-full overflow-visible">
                              <table className="w-full overflow-x-auto caption-bottom text-sm">
                                <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                                  <tr className='bg-gray-100 border-b'>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr.<br></br>No.</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Meeting</th>
                                    {/* <th className="h-10 px-2 text-center font-medium text-muted-foreground">Agenda</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Recurrence</th> */}
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Date</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Time</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Organizer</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Participants</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Status</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Location</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Meeting Type</th>

                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Action Points</th>

                                  </tr>

                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                  {meetings.map((meeting, index) => {
                                    const actionPointsCount = meeting.action_points_total || 0;
                                    const completedActionPoints = meeting.action_points_completed || 0;

                                    return (
                                      <tr key={meeting.id} className="border-b  transition-colors hover:bg-gray-50">
                                        <td className="p-2 text-center align-middle">
                                          {(completedPage - 1) * pageSize + index + 1}
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="w-32 font-medium text-gray-900 cursor-pointer mx-auto">
                                                {truncateText(meeting.title, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="w-32 font-medium text-gray-900 mx-auto">{meeting.title || '-'}</p>
                                          )}
                                        </td>
                                        {/* <td className="p-2 text-center align-middle">
                                          {meeting.agenda && meeting.agenda.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.agenda}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-medium cursor-pointer mx-auto">
                                                {truncateText(meeting.agenda, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-medium">{meeting.agenda || '-'}</p>
                                          )}
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{formatRecurrence(meeting.recurrence)}</span>
                                        </td> */}
                                        <td className="p-2 text-center align-middle">
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium">{meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}</p>
                                          </div>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{meeting.organizer?.full_name}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <ResponsiveTooltip
                                            content={
                                              <div className="space-y-1">
                                                {meeting.organizer && (
                                                  <div className="text-xs">
                                                    <span className="font-semibold">Organizer: </span>
                                                    {meeting.organizer?.full_name || meeting.organizer?.name || 'Unknown'}
                                                  </div>
                                                )}
                                                {meeting.participants && meeting.participants.length > 0 && (
                                                  <div className="space-y-0.5">
                                                    <div className="text-xs font-semibold">Participants:</div>
                                                    {meeting.participants.map((participant, idx) => (
                                                      <div key={idx} className="text-xs">
                                                        {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            }
                                          >
                                            <div className="flex items-center justify-center gap-1 cursor-pointer">
                                              <Users className="w-4 h-4 text-gray-400" />
                                              <span className="text-sm">{meeting.participant_count}</span>
                                            </div>
                                          </ResponsiveTooltip>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                            {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                          </Badge>
                                        </td>
                                        <td className='p-2 align-middle text-center'>
                                          <div className="flex items-center justify-center gap-1">
                                            {meeting.location && meeting.location.length > 20 ? (
                                              <ResponsiveTooltip
                                                content={<p className="text-sm whitespace-pre-wrap">{meeting.location}</p>}
                                                className="max-w-md p-3"
                                              >
                                                <span className="text-xs cursor-pointer">
                                                  {truncateText(meeting.location, 20)}
                                                </span>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs">{meeting.location || '-'}</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className='p-2 align-middle text-center'>
                                          <Badge variant="outline" className="text-[10px] h-5 px-1 py-0 capitalize border-blue-200 bg-blue-50 text-blue-700">
                                            {meeting.meeting_type || '-'}
                                          </Badge>
                                        </td>


                                        <td className="p-2 text-center align-middle">
                                          <div className="flex items-center justify-center gap-2">
                                            {actionPointsCount > 0 ? (
                                              <ResponsiveTooltip content={<p className="text-xs">View action points ({actionPointsCount})</p>}>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={() => handleActionPointsViewModalOpen(meeting)}
                                                  className="h-8 w-8 p-0 hover:bg-blue-50"
                                                >
                                                  <Eye className="w-4 h-4 text-blue-600" />
                                                </Button>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs text-gray-400">None</span>
                                            )}
                                            {(meeting.organizer?.id === user?.id || user?.role === 'admin' || meeting.participants?.some(p => p.id === user?.id)) && meeting.can_add_mom && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleMOMModalOpen(meeting)}
                                                className="text-xs h-8 px-3"
                                              >
                                                <FileText className="w-4 h-4 mr-2" />
                                                MOM
                                              </Button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        {/* Mobile Accordion View */}
                        <div className="md:hidden">
                          <Accordion type="single" collapsible className="w-full space-y-2">
                            {meetings.map((meeting, index) => {
                              const actionPointsCount = meeting.action_points_total || 0;
                              const completedActionPoints = meeting.action_points_completed || 0;

                              return (
                                <AccordionItem key={meeting.id} value={`item-${meeting.id}`} className="border rounded-lg px-4 bg-white">
                                  <AccordionTrigger className="hover:no-underline py-4">
                                    <div className="flex items-center justify-between w-full pr-4">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                                          {(completedPage - 1) * pageSize + index + 1}.
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          {meeting.title && meeting.title.length > 10 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-semibold text-gray-900  cursor-pointer">
                                                {truncateText(meeting.title, 15)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                              {meeting.title || '-'}
                                            </p>
                                          )}
                                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                              {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                            </Badge>
                                            <span className="text-xs text-gray-500">
                                              {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd') : '-'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="pb-4 pt-0">
                                    <div className="space-y-4  gap-2 pt-2 border-t">
                                      {/* Meeting Title */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                                          <p className="text-sm   text-gray-900">{meeting.title || '-'}</p>
                                        </div>


                                        {/* Recurrence */}
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Recurrence</p>
                                          <p className="text-sm text-gray-900">{formatRecurrence(meeting.recurrence)}</p>
                                        </div>
                                      </div>
                                      {/* Agenda */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Agenda</p>
                                          {meeting.agenda && meeting.agenda.length > 10 ? (
                                            <Popover>
                                              <PopoverTrigger asChild>
                                                <button className="text-sm text-gray-900 text-left hover:underline cursor-pointer">
                                                  {truncateText(meeting.agenda, 10)}
                                                </button>
                                              </PopoverTrigger>
                                              <PopoverContent className="w-72 p-3" align="start">
                                                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                                  {meeting.agenda}
                                                </div>
                                              </PopoverContent>
                                            </Popover>
                                          ) : (
                                            <p className="text-sm text-gray-900">{meeting.agenda || '-'}</p>
                                          )}

                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1 flex flex-wrap gap-1">Participants  <Users className="w-4 h-4 text-gray-400" /></p>
                                          <div className="flex items-center gap-2">

                                            <span className="text-sm text-gray-900">{meeting.participant_count || 0}</span>
                                            {meeting.participants && meeting.participants.length > 0 && (
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <button className="text-xs text-blue-600 hover:underline cursor-pointer">
                                                    (View list)
                                                  </button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-64 p-3" align="start">
                                                  <div className="space-y-2">

                                                    {meeting.participants.length > 0 && (
                                                      <div className="space-y-1">
                                                        <div className="text-xs font-semibold mb-1">Participants:</div>
                                                        {meeting.participants.map((participant, idx) => (
                                                          <div key={idx} className="text-xs pl-2">
                                                            {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                </PopoverContent>
                                              </Popover>
                                            )}
                                          </div>
                                        </div>

                                      </div>


                                      <div className='flex flex-wrap justify-between gap-2'>
                                        {/* Date */}
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Date</p>
                                          <p className="text-sm  text-gray-900">
                                            {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}
                                          </p>
                                        </div>

                                        {/* Time */}
                                        <div className='text-center'>
                                          <p className="text-xs font-medium text-gray-500 mb-1 flex gap-1 justify-center items-center">Time <Clock className="w-3 h-3 text-gray-400" /></p>
                                          <p className="text-xs text-gray-900">
                                            {formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}
                                          </p>
                                        </div>

                                      </div>


                                      {/* Organizer */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Organizer</p>
                                          <p className="text-sm text-gray-900">{meeting.organizer?.full_name || '-'}</p>
                                        </div>

                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Action Points</p>
                                          <div className="flex items-center gap-2">
                                            {actionPointsCount > 0 ? (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleActionPointsViewModalOpen(meeting)}
                                                className="h-8   hover:bg-blue-50 text-blue-600"
                                              >
                                                <span className="text-xs flex gap-1">
                                                  <Eye className="w-4 h-4 " />

                                                  ({actionPointsCount})
                                                </span>
                                              </Button>
                                            ) : (
                                              <span className="text-xs text-gray-400">None</span>
                                            )}
                                            {(meeting.organizer?.id === user?.id || user?.role === 'admin' || meeting.participants?.some(p => p.id === user?.id)) && meeting.can_add_mom && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleMOMModalOpen(meeting)}
                                                className="text-xs h-8 px-3"
                                              >
                                                <FileText className="w-4 h-4 mr-2" />
                                                MOM
                                              </Button>
                                            )}
                                          </div>
                                        </div>


                                      </div>

                                      {/* Action Points */}

                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              );
                            })}
                          </Accordion>
                        </div>

                        {/* Pagination - Always show if there's data */}
                        {completedFilteredTotal > 0 && (
                          <div className="mt-6 border-t ">
                            <Pagination
                              page={completedPage}
                              pages={completedFilteredPages}
                              total={completedFilteredTotal}
                              pageSize={pageSize}
                              hasNext={completedPage < completedFilteredPages}
                              hasPrev={completedPage > 1}
                              onPageChange={setCompletedPage}
                              onPageSizeChange={handlePageSizeChange}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="cancelled" className="space-y-6">
                <Card className="glass-card border-0 ">

                  <CardContent className='mt-2'>
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="loading-shimmer h-32 w-full rounded-lg"></div>
                      </div>
                    ) : meetings.length === 0 ? (
                      <div className="text-center py-8" data-testid="no-cancelled-meetings-message">
                        <XCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No cancelled meetings yet</p>
                        <p className="text-sm text-gray-400 mt-1">Cancelled meetings will appear here</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table View */}
                        <div className="hidden md:block rounded-lg border overflow-x-auto">
                          <div className="max-h-[calc(100vh-370px)] overflow-auto">
                            <div className="relative w-full overflow-visible">
                              <table className="w-full overflow-x-auto caption-bottom text-sm">
                                <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                                  <tr className='bg-gray-100 border-b'>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr.<br></br>No.</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Meeting</th>
                                    {/* <th className="h-10 px-2 text-center font-medium text-muted-foreground">Agenda</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Recurrence</th> */}
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Date</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Time</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Organizer</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Participants</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Status</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Location</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground text-nowrap">Meeting Type</th>
                                    <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Action Points</th>
                                  </tr>
                                </thead>
                                <tbody className="[&_tr:last-child]:border-0">
                                  {meetings.map((meeting, index) => {
                                    const actionPointsCount = meeting.action_points_total || 0;
                                    const completedActionPoints = meeting.action_points_completed || 0;

                                    return (
                                      <tr key={meeting.id} className="border-b  transition-colors hover:bg-gray-50">
                                        <td className="p-2 text-center align-middle">
                                          {(cancelledPage - 1) * pageSize + index + 1}
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          {meeting.title && meeting.title.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="w-32 font-medium text-gray-900 cursor-pointer mx-auto">
                                                {truncateText(meeting.title, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="w-32 font-medium text-gray-900 mx-auto">{meeting.title || '-'}</p>
                                          )}
                                        </td>
                                        {/* <td className="p-2 text-center align-middle">
                                          {meeting.agenda && meeting.agenda.length > 20 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.agenda}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-medium cursor-pointer mx-auto">
                                                {truncateText(meeting.agenda, 20)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-medium">{meeting.agenda || '-'}</p>
                                          )}
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{formatRecurrence(meeting.recurrence)}</span>
                                        </td> */}
                                        <td className="p-2 text-center align-middle">
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium">{meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}</p>
                                          </div>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <span className="text-sm font-medium">{meeting.organizer?.full_name}</span>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <ResponsiveTooltip
                                            content={
                                              <div className="space-y-1">
                                                {meeting.organizer && (
                                                  <div className="text-xs">
                                                    <span className="font-semibold">Organizer: </span>
                                                    {meeting.organizer?.full_name || meeting.organizer?.name || 'Unknown'}
                                                  </div>
                                                )}
                                                {meeting.participants && meeting.participants.length > 0 && (
                                                  <div className="space-y-0.5">
                                                    <div className="text-xs font-semibold">Participants:</div>
                                                    {meeting.participants.map((participant, idx) => (
                                                      <div key={idx} className="text-xs">
                                                        {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            }
                                          >
                                            <div className="flex items-center justify-center gap-1 cursor-pointer">
                                              <Users className="w-4 h-4 text-gray-400" />
                                              <span className="text-sm">{meeting.participant_count}</span>
                                            </div>
                                          </ResponsiveTooltip>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                            {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                          </Badge>
                                        </td>
                                        <td className='p-2 align-middle text-center'>
                                          <div className="flex items-center justify-center gap-1">
                                            {meeting.location && meeting.location.length > 20 ? (
                                              <ResponsiveTooltip
                                                content={<p className="text-sm whitespace-pre-wrap">{meeting.location}</p>}
                                                className="max-w-md p-3"
                                              >
                                                <span className="text-xs cursor-pointer">
                                                  {truncateText(meeting.location, 20)}
                                                </span>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs">{meeting.location || '-'}</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className='p-2 align-middle text-center'>
                                          <Badge variant="outline" className="text-[10px] h-5 px-1 py-0 capitalize border-blue-200 bg-blue-50 text-blue-700">
                                            {meeting.meeting_type || '-'}
                                          </Badge>
                                        </td>
                                        <td className="p-2 text-center align-middle">
                                          <div className="flex items-center justify-center gap-2">
                                            {actionPointsCount > 0 ? (
                                              <ResponsiveTooltip content={<p className="text-xs">View action points ({actionPointsCount})</p>}>
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={() => handleActionPointsViewModalOpen(meeting)}
                                                  className="h-8 w-8 p-0 hover:bg-blue-50"
                                                >
                                                  <Eye className="w-4 h-4 text-blue-600" />
                                                </Button>
                                              </ResponsiveTooltip>
                                            ) : (
                                              <span className="text-xs text-gray-400">None</span>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>

                        {/* Mobile Accordion View */}
                        <div className="md:hidden">
                          <Accordion type="single" collapsible className="w-full space-y-2">
                            {meetings.map((meeting, index) => {
                              const actionPointsCount = meeting.action_points_total || 0;
                              const completedActionPoints = meeting.action_points_completed || 0;

                              return (
                                <AccordionItem key={meeting.id} value={`item-${meeting.id}`} className="border rounded-lg px-4 bg-white">
                                  <AccordionTrigger className="hover:no-underline py-4">
                                    <div className="flex items-center justify-between w-full pr-4">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                                          {(cancelledPage - 1) * pageSize + index + 1}.
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          {meeting.title && meeting.title.length > 10 ? (
                                            <ResponsiveTooltip
                                              content={<p className="text-sm whitespace-pre-wrap">{meeting.title}</p>}
                                              className="max-w-md p-3"
                                            >
                                              <p className="text-sm font-semibold text-gray-900  cursor-pointer">
                                                {truncateText(meeting.title, 15)}
                                              </p>
                                            </ResponsiveTooltip>
                                          ) : (
                                            <p className="text-sm font-semibold text-gray-900 truncate">
                                              {meeting.title || '-'}
                                            </p>
                                          )}
                                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                                            <Badge variant="outline" className={`${getStatusBadgeColor(meeting.state)} text-xs`}>
                                              {meeting.state.charAt(0).toUpperCase() + meeting.state.slice(1)}
                                            </Badge>
                                            <span className="text-xs text-gray-500">
                                              {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd') : '-'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent className="pb-4 pt-0">
                                    <div className="space-y-4  gap-2 pt-2 border-t">
                                      {/* Meeting Title */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                                          <p className="text-sm   text-gray-900">{meeting.title || '-'}</p>
                                        </div>


                                        {/* Recurrence */}
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Recurrence</p>
                                          <p className="text-sm text-gray-900">{formatRecurrence(meeting.recurrence)}</p>
                                        </div>
                                      </div>
                                      {/* Agenda */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Agenda</p>
                                          {meeting.agenda && meeting.agenda.length > 10 ? (
                                            <Popover>
                                              <PopoverTrigger asChild>
                                                <button className="text-sm text-gray-900 text-left hover:underline cursor-pointer">
                                                  {truncateText(meeting.agenda, 10)}
                                                </button>
                                              </PopoverTrigger>
                                              <PopoverContent className="w-72 p-3" align="start">
                                                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                                  {meeting.agenda}
                                                </div>
                                              </PopoverContent>
                                            </Popover>
                                          ) : (
                                            <p className="text-sm text-gray-900">{meeting.agenda || '-'}</p>
                                          )}

                                        </div>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1 flex flex-wrap gap-1">Participants  <Users className="w-4 h-4 text-gray-400" /></p>
                                          <div className="flex items-center gap-2">

                                            <span className="text-sm text-gray-900">{meeting.participant_count || 0}</span>
                                            {meeting.participants && meeting.participants.length > 0 && (
                                              <Popover>
                                                <PopoverTrigger asChild>
                                                  <button className="text-xs text-blue-600 hover:underline cursor-pointer">
                                                    (View list)
                                                  </button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-64 p-3" align="start">
                                                  <div className="space-y-2">

                                                    {meeting.participants.length > 0 && (
                                                      <div className="space-y-1">
                                                        <div className="text-xs font-semibold mb-1">Participants:</div>
                                                        {meeting.participants.map((participant, idx) => (
                                                          <div key={idx} className="text-xs pl-2">
                                                            {participant?.full_name || participant?.name || participant?.email || 'Unknown'}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    )}
                                                  </div>
                                                </PopoverContent>
                                              </Popover>
                                            )}
                                          </div>
                                        </div>

                                      </div>


                                      <div className='flex flex-wrap justify-between gap-2'>
                                        {/* Date */}
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Date</p>
                                          <p className="text-sm  text-gray-900">
                                            {meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMM dd, yyyy') : '-'}
                                          </p>
                                        </div>

                                        {/* Time */}
                                        <div className='text-center'>
                                          <p className="text-xs font-medium text-gray-500 mb-1 flex gap-1 justify-center items-center">Time <Clock className="w-3 h-3 text-gray-400" /></p>
                                          <p className="text-xs text-gray-900">
                                            {formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}
                                          </p>
                                        </div>

                                      </div>

                                      {/* Organizer */}
                                      <div className='flex flex-wrap justify-between gap-2'>
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Organizer</p>
                                          <p className="text-sm text-gray-900">{meeting.organizer?.full_name || '-'}</p>
                                        </div>

                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-1">Action Points</p>
                                          <div className="flex items-center gap-2">
                                            {actionPointsCount > 0 ? (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => handleActionPointsViewModalOpen(meeting)}
                                                className="h-8   hover:bg-blue-50 text-blue-600"
                                              >
                                                <span className="text-xs flex gap-1">
                                                  <Eye className="w-4 h-4 " />

                                                  ({actionPointsCount})
                                                </span>
                                              </Button>
                                            ) : (
                                              <span className="text-xs text-gray-400">None</span>
                                            )}
                                          </div>
                                        </div>


                                      </div>

                                      {/* Action Points */}

                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              );
                            })}
                          </Accordion>
                        </div>

                        {/* Pagination - Always show if there's data */}
                        {cancelledFilteredTotal > 0 && (
                          <div className="mt-6 border-t ">
                            <Pagination
                              page={cancelledPage}
                              pages={cancelledFilteredPages}
                              total={cancelledFilteredTotal}
                              pageSize={pageSize}
                              hasNext={cancelledPage < cancelledFilteredPages}
                              hasPrev={cancelledPage > 1}
                              onPageChange={setCancelledPage}
                              onPageSizeChange={handlePageSizeChange}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="calendar">
            <CalendarView />
          </TabsContent>

          <TabsContent value="tasks">
            <MyTaskManagement
              subtab={urlSubtab}
              taskId={urlTaskId}
            />
          </TabsContent>

          <TabsContent value="review" className="space-y-6">
            <ReviewTasksTable
              taskId={urlTaskId}
            />
          </TabsContent>
        </Tabs>
      </div>

      <CreateMeetingModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateMeeting}
      />

      <MOMModal
        open={isMOMModalOpen}
        onClose={() => setIsMOMModalOpen(false)}
        meeting={selectedMeeting}
        users={allUsers}
        onSuccess={handleMOMModalSuccess}
      />

      <ActionPointsViewModal
        open={isActionPointsViewModalOpen}
        onClose={() => setIsActionPointsViewModalOpen(false)}
        meeting={selectedMeeting}
      />
      <EditMeetingModal
        open={isEditMeetingModalOpen}
        onClose={() => {
          setIsEditMeetingModalOpen(false);
          setMeetingToEdit(null);
        }}
        meeting={meetingToEdit}
        onSubmit={handleUpdateMeeting}
        users={allUsers}
      />

      <AlertDialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Meeting</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel the meeting "{meetingToCancel?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setMeetingToCancel(null)}>No, Keep Meeting</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelMeeting}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Cancel Meeting
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CancelRecurringMeetingDialog
        open={isRecurringCancelDialogOpen}
        onClose={() => {
          setIsRecurringCancelDialogOpen(false);
          setMeetingToCancel(null);
        }}
        meeting={meetingToCancel}
        onConfirm={handleRecurringCancelConfirm}
        isLoading={isCancelLoading}
      />
    </Layout>
  );
};

export default EmployeeDashboard;